import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/App';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Upload, Download, Trash2, Edit2, FileSpreadsheet, ArrowRight, ArrowLeft, RefreshCw, Mail, CheckCircle2, Circle, AlertTriangle, Home } from 'lucide-react';
import { POWizardV2 } from './POWizardV2';
import { MFPLBankMasterModal, MFPLBankAssignedCard } from './MFPLBankMaster';
import { FranchiseConfigurationTab } from './FranchiseConfigurationTab';
import { CategoryInvoiceWizard } from './CategoryInvoiceWizard';
import { FranchiseSetupTab, SnapshotEditor, GenerateInvoiceWizard } from './FranchiseSetupTab';
import { InteriorAssetModal } from './InteriorAssetModal';
import { VendorInvoicesCard } from '../components/vendor-invoices/VendorInvoicesCard';
import { SectionSetupModal } from './SectionSetupModal';

const API = process.env.REACT_APP_BACKEND_URL;
const CLASSIFICATIONS = ['ASSET', 'INVENTORY', 'SERVICE', 'FEE', 'DEPOSIT', 'WORKING_CAPITAL', 'EXPENSE', 'THIRD_PARTY_PURCHASE', 'OTHER'];
const BILLING_SOURCES = ['MFPL', 'FRANCHISEE_DIRECT', 'THIRD_PARTY_VENDOR'];
const CURRENCIES = ['INR', 'AUD', 'USD', 'GBP', 'AED', 'SGD', 'EUR', 'JPY'];
const TAX_REGIMES = ['GST_INDIA', 'GST_AUSTRALIA', 'VAT_UK', 'VAT_UAE', 'SALES_TAX_US', 'NONE'];

const currency = (v, code = 'INR') => {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(v || 0); }
  catch { return `${code} ${(v || 0).toLocaleString()}`; }
};

// iter238 — every call must carry the authenticated session token from
// the app-level useAuth() context.  Reading from localStorage returned
// null in production and every request 401'd.  A shared factory keeps
// the token binding in one place.
const makeCall = (token) => async (path, body, prefix) => {
  const base = prefix ? `${API}${prefix}` : `${API}/api/franchise-onboarding`;
  const res = await fetch(`${base}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...body })
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
  return j;
};

/* ─────────────────────────  LIST  ─────────────────────────── */
// iter244 — status → workflow stage badge helper
const STAGE_ORDER = ['INITIATED', 'COMMERCIAL_SETUP', 'PROCUREMENT',
  'INVOICING', 'PRE_OPENING', 'READY_FOR_GO_LIVE', 'LIVE', 'ARCHIVED'];
const STAGE_LABEL = {
  INITIATED: 'INITIATED', COMMERCIAL_SETUP: 'COMMERCIAL SETUP',
  PROCUREMENT: 'PROCUREMENT', INVOICING: 'INVOICING',
  PRE_OPENING: 'PRE-OPENING', READY_FOR_GO_LIVE: 'READY FOR GO-LIVE',
  LIVE: 'LIVE', ARCHIVED: 'ARCHIVED',
};

/* iter262 — Permanent Delete modal for the Archive tab.
   Shown ONLY to Super Admin.  Requires:
     • non-blocked cascade (no LIVE / no financial history)
     • reason text
     • typed "DELETE" confirmation
   Renders a live count summary sourced from `/preview-cascade`. */
function PermanentDeleteModal({ target, impact, form, setForm, busy, onClose, onConfirm }) {
  const open = !!target;
  const canConfirm = impact && !impact.blocked && !impact.error &&
    form.reason.trim().length > 0 && form.typed === 'DELETE';
  return (
    <Dialog open={open} onOpenChange={() => !busy && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="permanent-delete-modal">
        <DialogHeader>
          <DialogTitle className="text-red-700 flex items-center gap-2">
            <Trash2 className="w-5 h-5" /> Permanently Delete {target?.franchise_code}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Confirmation dialog to permanently delete an archived franchise and all its
            connected records. Requires typed DELETE confirmation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-red-50 border border-red-300 rounded p-3 text-red-900">
            <b>Permanently delete this franchise?</b><br/>
            This will permanently remove the franchise and ALL connected records
            and <b>cannot be undone.</b><br/>
            <span className="text-xs opacity-80">
              Type <span className="font-mono bg-red-100 px-1">DELETE</span> to confirm.
            </span>
          </div>

          {!impact ? (
            <div className="text-xs text-slate-500 italic" data-testid="perm-delete-loading">
              Calculating cascade preview…
            </div>
          ) : impact.error ? (
            <div className="bg-rose-100 border border-rose-300 rounded p-2 text-xs text-rose-900"
                    data-testid="perm-delete-error">
              Preview failed: {impact.message || 'unknown error'}
            </div>
          ) : impact.blocked ? (
            <div className="bg-amber-100 border border-amber-400 rounded p-3 text-xs text-amber-900"
                    data-testid="perm-delete-blocked">
              <b>Cannot permanently delete.</b> This centre has LIVE status
              or genuine operational history.  Use the SA FORCE DELETE LIVE
              CENTRE flow (not part of this iteration).
              <ul className="list-disc pl-4 mt-1">
                {impact.any_live && <li>Status: <b>LIVE</b></li>}
                {Object.entries(impact.financial_history || {}).map(([k, v]) => (
                  <li key={k}>{k.replace(/_/g, ' ')}: <b>{v}</b></li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="bg-slate-50 border rounded p-3 text-xs" data-testid="perm-delete-preview">
              <b>The following records will be permanently removed:</b>
              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                {Object.entries(impact.cascade || {})
                  .filter(([, v]) => v > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <div key={k} data-testid={`perm-delete-count-${k}`}>
                      {k.replace(/_/g, ' ')}: <b>{v}</b>
                    </div>
                  ))}
              </div>
              <div className="mt-2 pt-2 border-t text-slate-600">
                Total rows: <b data-testid="perm-delete-total">{impact.total_rows || 0}</b> ·
                Onboardings: <b>{(impact.onboarding_ids || []).length}</b> ·
                Centres to prune: <b>{(impact.centre_codes || []).length}</b>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">
                Global master data (item, vendor, PO/Invoice masters, company
                master) is <b>not</b> touched.
              </div>
            </div>
          )}

          <div>
            <Label>Reason <span className="text-red-500">*</span></Label>
            <Textarea rows={2} value={form.reason}
                       disabled={busy || impact?.blocked || impact?.error}
                       onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                       data-testid="perm-delete-reason" />
          </div>
          <div>
            <Label>Type <span className="font-mono bg-red-100 px-1">DELETE</span> to confirm</Label>
            <Input value={form.typed}
                   disabled={busy || impact?.blocked || impact?.error}
                   onChange={e => setForm(f => ({ ...f, typed: e.target.value }))}
                   data-testid="perm-delete-typed" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive"
                    disabled={!canConfirm || busy}
                    onClick={onConfirm}
                    data-testid="perm-delete-confirm-btn">
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnboardingList({ call, onOpen, onCreate }) {
  const { session } = useAuth();
  const isSA = !!session?.is_super_admin;
  const token = session?.token;
  const [showBankMaster, setShowBankMaster] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active'); // active | completed | archive
  const [openMenu, setOpenMenu] = useState(null); // onboarding_id currently showing ⋮ menu
  // Danger-Zone (opens directly from row)
  const [dangerFor, setDangerFor] = useState(null); // full onboarding row
  const [dangerImpact, setDangerImpact] = useState(null);
  const [dangerForm, setDangerForm] = useState({ reason: '', typed: '', acknowledged: false });
  // iter262 — Permanent Delete (Archive tab, SA only)
  const [permDeleteFor, setPermDeleteFor] = useState(null);
  const [permDeleteImpact, setPermDeleteImpact] = useState(null);
  const [permDeleteForm, setPermDeleteForm] = useState({ reason: '', typed: '' });
  const [permDeleteBusy, setPermDeleteBusy] = useState(false);

  // Load cascade preview when a row is selected for permanent delete.
  useEffect(() => {
    if (!permDeleteFor) return;
    (async () => {
      try {
        const r = await call(`/preview-cascade/${permDeleteFor.franchise_code}`,
                                     {}, '/api/franchises');
        setPermDeleteImpact(r);
      } catch (e) {
        setPermDeleteImpact({ error: true, message: e.message });
      }
    })();
  }, [permDeleteFor, call]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await call('/list', {
        include_deleted: tab === 'archive',
      });
      setItems(r.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [call, tab]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(o => {
    if (tab === 'active') return !o.deleted && o.status !== 'LIVE' && o.status !== 'COMPLETED';
    if (tab === 'completed') return !o.deleted && (o.status === 'LIVE' || o.status === 'COMPLETED');
    if (tab === 'archive') return !!o.deleted;
    return true;
  });

  const stagePct = (status) => {
    const idx = STAGE_ORDER.indexOf(status);
    return idx < 0 ? 0 : Math.round(((idx + 1) / STAGE_ORDER.length) * 100);
  };

  const openDanger = async (o) => {
    setOpenMenu(null);
    try {
      const impact = await call(`/danger/delete-impact/${o.onboarding_id}`, {});
      setDangerFor(o); setDangerImpact(impact);
      setDangerForm({ reason: '', typed: '', acknowledged: false });
    } catch (e) { toast.error(e.message); }
  };

  const confirmDelete = async () => {
    if (!dangerFor) return;
    try {
      await call(`/danger/delete-onboarding/${dangerFor.onboarding_id}`, {
        reason: dangerForm.reason.trim(),
        typed_confirmation: dangerForm.typed,
        acknowledged: dangerForm.acknowledged,
      });
      toast.success(`Onboarding ${dangerFor.franchise_code} archived`);
      setDangerFor(null); setDangerImpact(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="onboarding-list-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Franchise Onboardings</CardTitle>
        <div className="flex gap-2 items-center">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-9">
              <TabsTrigger value="active" className="text-xs" data-testid="list-tab-active">Active</TabsTrigger>
              <TabsTrigger value="completed" className="text-xs" data-testid="list-tab-completed">Completed</TabsTrigger>
              <TabsTrigger value="archive" className="text-xs" data-testid="list-tab-archive">Archive</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" onClick={load} data-testid="btn-refresh-list"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          {isSA && <Button variant="outline" onClick={() => setShowBankMaster(true)} data-testid="btn-open-bank-master">MFPL Banks</Button>}
          <Button onClick={onCreate} data-testid="btn-create-new-franchise"><Plus className="w-4 h-4 mr-1" />Create New Franchise</Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
          filtered.length === 0 ? <div className="text-sm text-muted-foreground py-8 text-center" data-testid="empty-list">No {tab} onboardings.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left border-b">
                  <tr><th className="p-2">Onboarding ID</th><th>Franchise</th><th>Centre</th><th>Status</th><th className="w-32">Progress</th><th>Created</th><th className="text-right pr-3">Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map(o => (
                    <tr key={o.onboarding_id} className={`border-b hover:bg-muted/40 ${o.deleted ? 'bg-red-50/40' : ''}`} data-testid={`onboarding-row-${o.onboarding_id}`}>
                      <td className="p-2 font-mono text-xs">{o.onboarding_id}</td>
                      <td>{o.franchise_code} — {o.franchise_name}</td>
                      <td>{o.centre_code}</td>
                      <td><Badge variant="outline">{STAGE_LABEL[o.status] || o.status}</Badge></td>
                      <td>
                        <div className="w-24 h-2 bg-muted rounded overflow-hidden" title={`${stagePct(o.status)}%`}>
                          <div className="h-full bg-primary" style={{ width: `${stagePct(o.status)}%` }}></div>
                        </div>
                        <div className="text-[10px] text-muted-foreground">{stagePct(o.status)}%</div>
                      </td>
                      <td className="text-xs">{(o.created_at || '').slice(0, 10)}</td>
                      <td className="text-right pr-3">
                        {o.deleted ? (
                          <div className="inline-flex items-center gap-2">
                            <span className="text-xs text-red-600 italic" title={o.deletion_reason}>
                              Deleted · {(o.deleted_at || '').slice(0, 10)}
                            </span>
                            {/* iter262 — Permanent delete from Archive */}
                            {isSA && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setPermDeleteFor(o)}
                                className="text-red-700 hover:bg-red-100 h-6 px-2"
                                data-testid={`btn-perm-delete-${o.onboarding_id}`}
                              >
                                <Trash2 className="w-3 h-3 mr-1" /> Delete Permanently
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1 relative">
                            <Button size="sm" variant="ghost" onClick={() => onOpen(o.onboarding_id)} data-testid={`btn-open-${o.onboarding_id}`}>Open <ArrowRight className="w-4 h-4 ml-1" /></Button>
                            {isSA && (
                              <div className="relative">
                                <Button size="sm" variant="ghost" onClick={() => setOpenMenu(openMenu === o.onboarding_id ? null : o.onboarding_id)} data-testid={`btn-menu-${o.onboarding_id}`}>⋮</Button>
                                {openMenu === o.onboarding_id && (
                                  <div className="absolute right-0 top-8 z-10 bg-white border rounded shadow-lg min-w-[220px]" data-testid={`menu-${o.onboarding_id}`}>
                                    <button
                                      className="w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
                                      onClick={() => openDanger(o)}
                                      data-testid={`btn-delete-onboarding-${o.onboarding_id}`}
                                    >
                                      <Trash2 className="w-3 h-3" /> Delete Entire Onboarding
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </CardContent>

      {/* Danger-Zone confirmation modal (row-launched) */}
      <Dialog open={!!dangerFor} onOpenChange={() => setDangerFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="list-danger-modal">
          <DialogHeader><DialogTitle className="text-red-700">Delete Entire Onboarding — {dangerFor?.franchise_code}</DialogTitle></DialogHeader>
          {dangerImpact?.operational_data_exists ? (
            <div className="bg-amber-100 border border-amber-400 rounded p-3 text-sm text-amber-900" data-testid="list-danger-blocked">
              <b>Centre has operational transactions and cannot be deleted through Franchise Onboarding.</b>
              <div className="mt-2 text-xs">
                {Object.entries(dangerImpact.operational_data).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 border rounded p-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><b>Onboarding ID:</b> <span className="font-mono">{dangerFor?.onboarding_id}</span></div>
                  <div><b>Current Status:</b> {STAGE_LABEL[dangerFor?.status] || dangerFor?.status}</div>
                  <div><b>Franchise Code:</b> {dangerFor?.franchise_code}</div>
                  <div><b>Franchise Name:</b> {dangerImpact?.franchise_name || dangerFor?.franchise_name || '—'}</div>
                  <div><b>Centre Code:</b> {dangerFor?.centre_code}</div>
                  <div><b>Centre Name:</b> {dangerImpact?.centre_name || dangerFor?.centre_name || '—'}</div>
                </div>
              </div>
              <div className="bg-red-50 border border-red-300 rounded p-2 text-xs text-red-900" data-testid="list-danger-impact">
                <b>Dependency Summary:</b>
                <ul className="list-disc pl-4 mt-1 grid grid-cols-2 gap-x-2">
                  {dangerImpact && Object.entries(dangerImpact.counts).map(([k, v]) => (
                    <li key={k}>{k.replace(/_/g, ' ')}: <b>{v}</b></li>
                  ))}
                </ul>
              </div>
              <div>
                <Label>Reason <span className="text-red-500">*</span></Label>
                <Textarea rows={2} value={dangerForm.reason} onChange={e => setDangerForm(f => ({ ...f, reason: e.target.value }))} data-testid="list-danger-reason" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={dangerForm.acknowledged} onChange={e => setDangerForm(f => ({ ...f, acknowledged: e.target.checked }))} data-testid="list-danger-ack" />
                <label className="text-xs">I understand this onboarding and its related pre-opening records will be removed from all active views.</label>
              </div>
              <div>
                <Label>Type <span className="font-mono bg-red-100 px-1">DELETE {dangerFor?.franchise_code}</span> to confirm</Label>
                <Input value={dangerForm.typed} onChange={e => setDangerForm(f => ({ ...f, typed: e.target.value }))} data-testid="list-danger-typed" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDangerFor(null)}>Cancel</Button>
            {!dangerImpact?.operational_data_exists && (
              <Button variant="destructive"
                disabled={!dangerForm.acknowledged || !dangerForm.reason.trim() || dangerForm.typed !== `DELETE ${dangerFor?.franchise_code}`}
                onClick={confirmDelete}
                data-testid="list-btn-danger-confirm">
                Delete Entire Onboarding
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MFPLBankMasterModal open={showBankMaster} onClose={() => setShowBankMaster(false)} token={token} isSA={isSA} />

      {/* iter262 — Permanent Delete modal (Archive tab, SA only) */}
      <PermanentDeleteModal
        target={permDeleteFor}
        impact={permDeleteImpact}
        form={permDeleteForm}
        setForm={setPermDeleteForm}
        busy={permDeleteBusy}
        onClose={() => { setPermDeleteFor(null); setPermDeleteImpact(null); setPermDeleteForm({ reason: '', typed: '' }); }}
        onConfirm={async () => {
          if (!permDeleteFor) return;
          setPermDeleteBusy(true);
          try {
            const r = await call(
              `/permanent-delete/${permDeleteFor.franchise_code}`,
              {
                confirmation: 'DELETE',
                reason: permDeleteForm.reason.trim(),
              },
              '/api/franchises',
            );
            toast.success(
              `${permDeleteFor.franchise_code} permanently deleted — ` +
              `${r.total_rows || 0} row(s) wiped.`);
            setPermDeleteFor(null); setPermDeleteImpact(null);
            setPermDeleteForm({ reason: '', typed: '' });
            load();
          } catch (e) {
            toast.error(e.message || 'Delete failed');
          } finally { setPermDeleteBusy(false); }
        }}
      />
    </Card>
  );
}

/* ─────────────────────────  CREATE MODAL  ─────────────────────────── */
function CreateFranchiseModal({ call, open, onClose, onCreated }) {
  const [form, setForm] = useState({
    franchise_code: '', franchise_name: '', legal_entity_name: '',
    primary_contact_name: '', primary_contact_email: '', primary_contact_phone: '',
    city: '', state: '', country: 'India', pincode: '',
    gstin: '', pan: '', agreement_date: '', agreement_start_date: '',
    lock_in_years: 3, agreement_duration_years: 7,
    franchise_fee: 0, security_deposit: 0, initial_working_capital: 0,
    expected_go_live_date: '', centre_type: 'DINE_IN', area_sqft: 0,
    currency_code: 'INR', tax_regime: 'GST_INDIA',
    template_key: 'STANDARD_DINE_IN'
  });
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { call('/templates/list', {}).then(r => setTemplates(r.items || [])).catch(() => { }); }
  }, [open]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    if (!form.franchise_code || !form.franchise_name) { toast.error('Franchise Code and Name are required'); return; }
    setSaving(true);
    try {
      const r = await call('/create', form);
      toast.success('Franchise onboarding created');
      onCreated(r.onboarding.onboarding_id);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-franchise-modal">
        <DialogHeader><DialogTitle>Create New Franchise & Onboarding</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-3">
          <div><Label>Franchise Code *</Label><Input value={form.franchise_code} onChange={e => set('franchise_code', e.target.value.toUpperCase())} placeholder="e.g. FR-KALYAN02" data-testid="input-franchise-code" /></div>
          <div><Label>Franchise Name *</Label><Input value={form.franchise_name} onChange={e => set('franchise_name', e.target.value)} placeholder="e.g. Purnabramha Kalyan" data-testid="input-franchise-name" /></div>
          <div className="col-span-2"><Label>Legal Entity Name</Label><Input value={form.legal_entity_name} onChange={e => set('legal_entity_name', e.target.value)} data-testid="input-legal-entity" /></div>
          <div><Label>Primary Contact Name</Label><Input value={form.primary_contact_name} onChange={e => set('primary_contact_name', e.target.value)} data-testid="input-contact-name" /></div>
          <div><Label>Contact Phone</Label><Input value={form.primary_contact_phone} onChange={e => set('primary_contact_phone', e.target.value)} data-testid="input-contact-phone" /></div>
          <div><Label>Contact Email</Label><Input value={form.primary_contact_email} onChange={e => set('primary_contact_email', e.target.value)} data-testid="input-contact-email" /></div>
          <div><Label>Country</Label>
            <Select value={form.country} onValueChange={v => set('country', v)}>
              <SelectTrigger data-testid="select-country"><SelectValue /></SelectTrigger>
              <SelectContent>{['India', 'Australia', 'USA', 'UK', 'UAE', 'Japan', 'Singapore'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>City</Label><Input value={form.city} onChange={e => set('city', e.target.value)} data-testid="input-city" /></div>
          <div><Label>State</Label><Input value={form.state} onChange={e => set('state', e.target.value)} data-testid="input-state" /></div>
          <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => set('pincode', e.target.value)} data-testid="input-pincode" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin} onChange={e => set('gstin', e.target.value.toUpperCase())} data-testid="input-gstin" /></div>
          <div><Label>PAN</Label><Input value={form.pan} onChange={e => set('pan', e.target.value.toUpperCase())} data-testid="input-pan" /></div>
          <div><Label>Agreement Date</Label><Input type="date" value={form.agreement_date} onChange={e => set('agreement_date', e.target.value)} data-testid="input-agreement-date" /></div>
          <div><Label>Agreement Start Date</Label><Input type="date" value={form.agreement_start_date} onChange={e => set('agreement_start_date', e.target.value)} data-testid="input-agreement-start-date" /></div>
          <div><Label>Lock-in (yrs)</Label><Input type="number" value={form.lock_in_years} onChange={e => set('lock_in_years', +e.target.value || 0)} data-testid="input-lock-in" /></div>
          <div><Label>Duration (yrs)</Label><Input type="number" value={form.agreement_duration_years} onChange={e => set('agreement_duration_years', +e.target.value || 0)} data-testid="input-duration" /></div>
          <div><Label>Franchise Fee</Label><Input type="number" value={form.franchise_fee} onChange={e => set('franchise_fee', +e.target.value || 0)} data-testid="input-franchise-fee" /></div>
          <div><Label>Security Deposit</Label><Input type="number" value={form.security_deposit} onChange={e => set('security_deposit', +e.target.value || 0)} data-testid="input-security-deposit" /></div>
          <div><Label>Initial Working Capital</Label><Input type="number" value={form.initial_working_capital} onChange={e => set('initial_working_capital', +e.target.value || 0)} data-testid="input-working-capital" /></div>
          <div><Label>Expected Go-Live Date</Label><Input type="date" value={form.expected_go_live_date} onChange={e => set('expected_go_live_date', e.target.value)} data-testid="input-go-live-date" /></div>
          <div><Label>Centre Type</Label><Input value={form.centre_type} onChange={e => set('centre_type', e.target.value)} data-testid="input-centre-type" /></div>
          <div><Label>Area (sq ft)</Label><Input type="number" value={form.area_sqft} onChange={e => set('area_sqft', +e.target.value || 0)} data-testid="input-area" /></div>
          <div><Label>Currency</Label>
            <Select value={form.currency_code} onValueChange={v => set('currency_code', v)}>
              <SelectTrigger data-testid="select-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tax Regime</Label>
            <Select value={form.tax_regime} onValueChange={v => set('tax_regime', v)}>
              <SelectTrigger data-testid="select-tax-regime"><SelectValue /></SelectTrigger>
              <SelectContent>{TAX_REGIMES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Load Template</Label>
            <Select value={form.template_key || 'NONE'} onValueChange={v => set('template_key', v === 'NONE' ? '' : v)}>
              <SelectTrigger data-testid="select-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">-- Do not load a template --</SelectItem>
                {templates.map(t => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="btn-cancel-create">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-submit-create">{saving ? 'Creating…' : 'Create Franchise + Onboarding'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────  DASHBOARD TILE  ─────────────────────────── */
function DashboardTile({ dash, onOpenSection }) {
  if (!dash) return null;
  const ob = dash.onboarding || {};
  const cs = dash.commercial_summary || {};
  const statusColor = (st) => {
    if (st === 'COMPLETED' || st === 'DONE' || st === 'FROZEN' || st === 'NOT_APPLICABLE') return 'bg-green-600 text-white';
    if (st === 'IN_PROGRESS') return 'bg-amber-100 text-amber-900 border-amber-500';
    if (st === 'BLOCKED') return 'bg-red-100 text-red-800 border-red-500';
    return 'bg-muted text-muted-foreground';
  };
  return (
    <Card data-testid="onboarding-dashboard-tile">
      <CardHeader><CardTitle>Onboarding Progress — {ob.franchise_code}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="text-3xl font-bold" data-testid="overall-progress-pct">{dash.overall_pct}%</div>
          <div className="flex-1"><Progress value={dash.overall_pct} /></div>
          {dash.readiness?.ready_for_go_live
            ? <Badge className="bg-green-600" data-testid="badge-go-live-ready">READY FOR GO-LIVE</Badge>
            : <Badge variant="outline" className="text-red-600 border-red-600" data-testid="badge-not-ready">{dash.readiness?.mandatory_blockers?.length || 0} blockers</Badge>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="bg-muted/50 rounded p-2"><div className="text-xs text-muted-foreground">Setup Value</div><div className="font-semibold" data-testid="tile-setup-value">{currency(cs.total_setup_value, ob.currency_code)}</div></div>
          <div className="bg-muted/50 rounded p-2"><div className="text-xs text-muted-foreground">Working Capital</div><div className="font-semibold" data-testid="tile-wc-value">{currency(cs.total_working_capital, ob.currency_code)}</div></div>
          <div className="bg-muted/50 rounded p-2"><div className="text-xs text-muted-foreground">Heads</div><div className="font-semibold">{cs.total_heads || 0}</div></div>
          <div className="bg-muted/50 rounded p-2"><div className="text-xs text-muted-foreground">Line Items</div><div className="font-semibold">{cs.total_lines || 0}</div></div>
        </div>
        <div className="space-y-1" data-testid="dashboard-section-list">
          {dash.section_progress?.map(s => (
            <div key={s.key} className="border rounded p-2 text-xs" data-testid={`section-row-${s.key}`}>
              <div className="flex items-center gap-2">
                <div className="w-40 truncate font-medium">{s.label}{s.mandatory && <span className="text-red-500 ml-1">*</span>}</div>
                <Progress value={s.pct} className="flex-1 h-2" />
                {s.counts?.total > 0 && (
                  <span className="text-[10px] text-muted-foreground w-16 text-right" data-testid={`section-counts-${s.key}`}>{s.counts.done}/{s.counts.total}</span>
                )}
                <Badge className={`text-[10px] w-24 justify-center ${statusColor(s.status)}`} data-testid={`section-status-${s.key}`}>{s.status}</Badge>
                {onOpenSection && (
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onOpenSection(s.key)} data-testid={`btn-open-section-${s.key}`}>Open</Button>
                )}
              </div>
              {s.blockers?.length > 0 && (
                <ul className="mt-1 ml-40 text-[10px] text-red-700 list-disc list-inside space-y-0.5" data-testid={`section-blockers-${s.key}`}>
                  {s.blockers.slice(0, 3).map((b, i) => <li key={i}>{b.label}{b.detail ? ` — ${b.detail}` : ''}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────  COMMERCIAL SETUP GRID  ─────────────────────────── */
function CommercialSetup({ call, token, oid, heads, lines, onReload, currencyCode }) {
  const [editingLine, setEditingLine] = useState(null);
  const [addingLineFor, setAddingLineFor] = useState(null);
  const [editingHead, setEditingHead] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const fileRef = useRef(null);

  const linesByHead = React.useMemo(() => {
    const map = {};
    (lines || []).forEach(l => { (map[l.head_id] = map[l.head_id] || []).push(l); });
    return map;
  }, [lines]);

  const grandTotal = React.useMemo(() =>
    (lines || []).reduce((s, l) => s + (l.final_amount || 0), 0), [lines]);

  const saveHead = async (payload) => {
    try { await call('/heads/upsert', { onboarding_id: oid, ...payload }); toast.success('Head saved'); setEditingHead(null); onReload(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteHead = async (head_id) => {
    if (!window.confirm('Delete this commercial head and all its lines?')) return;
    try { await call('/heads/delete', { onboarding_id: oid, entity_id: head_id }); toast.success('Head deleted'); onReload(); }
    catch (e) { toast.error(e.message); }
  };
  const saveLine = async (payload) => {
    try { await call('/lines/upsert', { onboarding_id: oid, ...payload }); toast.success('Line saved'); setEditingLine(null); setAddingLineFor(null); onReload(); }
    catch (e) { toast.error(e.message); }
  };
  const deleteLine = async (line_id) => {
    if (!window.confirm('Delete this line item?')) return;
    try { await call('/lines/delete', { onboarding_id: oid, entity_id: line_id }); toast.success('Line deleted'); onReload(); }
    catch (e) { toast.error(e.message); }
  };

  const doTemplateDownload = async () => {
    try {
      const res = await fetch(`${API}/api/franchise-onboarding/excel/template-download`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = 'Onboarding_Import_Template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error(e.message); }
  };

  const onExcelSelected = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('token', token); fd.append('onboarding_id', oid); fd.append('file', file);
      const res = await fetch(`${API}/api/franchise-onboarding/excel/import`, { method: 'POST', body: fd });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Import failed');
      setImportPreview(j);
    } catch (err) { toast.error(err.message); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const commitImport = async () => {
    if (!importPreview) return;
    try { await call('/excel/commit', { onboarding_id: oid, heads: importPreview.heads }); toast.success('Import committed'); setImportPreview(null); onReload(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Commercial Setup</CardTitle>
        <div className="flex gap-2 items-center">
          <span className="text-sm text-muted-foreground">Grand Total: <b>{currency(grandTotal, currencyCode)}</b></span>
          <Button size="sm" variant="outline" onClick={doTemplateDownload} data-testid="btn-excel-template-download"><Download className="w-4 h-4 mr-1" />Template</Button>
          <input type="file" ref={fileRef} accept=".xlsx" className="hidden" onChange={onExcelSelected} data-testid="input-excel-file" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-excel-import"><Upload className="w-4 h-4 mr-1" />Import Excel</Button>
          <Button size="sm" onClick={() => setEditingHead({ name: '', classification: 'OTHER', sort_order: (heads.length + 1) * 10, active: true })} data-testid="btn-add-head"><Plus className="w-4 h-4 mr-1" />Add Head</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {(heads || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="empty-setup">No commercial heads yet. Load a template or click Add Head.</div>
        ) : (heads || []).map(h => (
          <div key={h.id} className="border rounded-md" data-testid={`head-block-${h.id}`}>
            <div className="flex items-center justify-between bg-muted/40 px-3 py-2 rounded-t-md">
              <div>
                <div className="font-semibold">{h.name} <Badge variant="outline" className="ml-2 text-xs">{h.classification}</Badge></div>
                <div className="text-xs text-muted-foreground">{(linesByHead[h.id] || []).length} line(s) · {currency((linesByHead[h.id] || []).reduce((s, l) => s + (l.final_amount || 0), 0), currencyCode)}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditingHead(h)} data-testid={`btn-edit-head-${h.id}`}><Edit2 className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => deleteHead(h.id)} data-testid={`btn-del-head-${h.id}`}><Trash2 className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => setAddingLineFor(h)} data-testid={`btn-add-line-${h.id}`}><Plus className="w-4 h-4 mr-1" />Line</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left">
                  <tr className="border-b"><th className="p-2">Sr</th><th>Code</th><th>Description</th><th>Cls</th><th>Qty</th><th>Rate</th><th>Disc</th><th>GST%</th><th>Final</th><th>Supplier</th><th>Billing</th><th></th></tr>
                </thead>
                <tbody>
                  {(linesByHead[h.id] || []).map((l, i) => (
                    <tr key={l.id} className="border-b hover:bg-muted/20" data-testid={`line-row-${l.id}`}>
                      <td className="p-2">{i + 1}</td><td>{l.item_code || '—'}</td><td>{l.description}</td>
                      <td><Badge variant="outline" className="text-[10px]">{l.classification}</Badge></td>
                      <td>{l.quantity} {l.unit}</td><td>{currency(l.rate, currencyCode)}</td>
                      <td>{currency(l.discount, currencyCode)}</td><td>{l.gst_percent}%</td>
                      <td className="font-semibold">{currency(l.final_amount, currencyCode)}</td>
                      <td>{l.supplier || '—'}</td><td className="text-[10px]">{l.billing_source}</td>
                      <td className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditingLine(l)} data-testid={`btn-edit-line-${l.id}`}><Edit2 className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteLine(l.id)} data-testid={`btn-del-line-${l.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </CardContent>

      {/* HEAD MODAL */}
      {editingHead && (
        <Dialog open={true} onOpenChange={() => setEditingHead(null)}>
          <DialogContent data-testid="head-modal">
            <DialogHeader><DialogTitle>{editingHead.id ? 'Edit Head' : 'Add Head'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editingHead.name} onChange={e => setEditingHead({ ...editingHead, name: e.target.value })} data-testid="input-head-name" /></div>
              <div><Label>Classification</Label>
                <Select value={editingHead.classification} onValueChange={v => setEditingHead({ ...editingHead, classification: v })}>
                  <SelectTrigger data-testid="select-head-cls"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Sort Order</Label><Input type="number" value={editingHead.sort_order} onChange={e => setEditingHead({ ...editingHead, sort_order: +e.target.value || 0 })} data-testid="input-head-sort" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingHead(null)}>Cancel</Button>
              <Button onClick={() => saveHead({ head_id: editingHead.id, name: editingHead.name, classification: editingHead.classification, sort_order: editingHead.sort_order, active: true })} data-testid="btn-save-head">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* LINE MODAL */}
      {(editingLine || addingLineFor) && (
        <LineModal
          line={editingLine || { head_id: addingLineFor.id, quantity: 1, unit: 'LS', rate: 0, discount: 0, gst_percent: 18, billing_source: 'MFPL', classification: addingLineFor?.classification || 'OTHER' }}
          onClose={() => { setEditingLine(null); setAddingLineFor(null); }}
          onSave={saveLine}
        />
      )}

      {/* IMPORT PREVIEW */}
      {importPreview && (
        <Dialog open={true} onOpenChange={() => setImportPreview(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-preview-modal">
            <DialogHeader><DialogTitle>Import Preview — {importPreview.total_heads} heads / {importPreview.total_lines} lines / {currency(importPreview.grand_total, currencyCode)}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {importPreview.heads.map((h, i) => (
                <div key={i} className="border rounded p-2">
                  <div className="font-semibold text-sm">{h.name} <Badge variant="outline" className="text-xs">{h.classification}</Badge> — {currency(h.total, currencyCode)}</div>
                  <div className="text-xs text-muted-foreground">{h.lines.length} line(s)</div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportPreview(null)} data-testid="btn-cancel-import">Cancel</Button>
              <Button onClick={commitImport} data-testid="btn-commit-import">Commit Import</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function LineModal({ line, onClose, onSave }) {
  const [f, setF] = useState({
    line_id: line.id, head_id: line.head_id,
    item_code: line.item_code || '', description: line.description || '',
    category: line.category || '', hsn_code: line.hsn_code || '',
    quantity: line.quantity || 0, unit: line.unit || 'LS',
    rate: line.rate || 0, discount: line.discount || 0,
    gst_percent: line.gst_percent || 0, supplier: line.supplier || '',
    billing_source: line.billing_source || 'MFPL',
    classification: line.classification || 'OTHER',
    remarks: line.remarks || ''
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const base = (+f.quantity || 0) * (+f.rate || 0);
  const taxable = Math.max(base - (+f.discount || 0), 0);
  const gst = taxable * (+f.gst_percent || 0) / 100;
  const finalAmt = taxable + gst;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="line-modal">
        <DialogHeader><DialogTitle>{line.id ? 'Edit Line' : 'Add Line'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Description</Label><Input value={f.description} onChange={e => set('description', e.target.value)} data-testid="input-line-desc" /></div>
          <div><Label>Item Code</Label><Input value={f.item_code} onChange={e => set('item_code', e.target.value)} data-testid="input-line-code" /></div>
          <div><Label>Category</Label><Input value={f.category} onChange={e => set('category', e.target.value)} data-testid="input-line-cat" /></div>
          <div><Label>HSN/SAC</Label><Input value={f.hsn_code} onChange={e => set('hsn_code', e.target.value)} data-testid="input-line-hsn" /></div>
          <div><Label>Classification</Label>
            <Select value={f.classification} onValueChange={v => set('classification', v)}>
              <SelectTrigger data-testid="select-line-cls"><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Quantity</Label><Input type="number" value={f.quantity} onChange={e => set('quantity', +e.target.value || 0)} data-testid="input-line-qty" /></div>
          <div><Label>Unit</Label><Input value={f.unit} onChange={e => set('unit', e.target.value)} data-testid="input-line-unit" /></div>
          <div><Label>Rate</Label><Input type="number" value={f.rate} onChange={e => set('rate', +e.target.value || 0)} data-testid="input-line-rate" /></div>
          <div><Label>Discount</Label><Input type="number" value={f.discount} onChange={e => set('discount', +e.target.value || 0)} data-testid="input-line-disc" /></div>
          <div><Label>GST %</Label><Input type="number" value={f.gst_percent} onChange={e => set('gst_percent', +e.target.value || 0)} data-testid="input-line-gst" /></div>
          <div><Label>Supplier</Label><Input value={f.supplier} onChange={e => set('supplier', e.target.value)} data-testid="input-line-supplier" /></div>
          <div><Label>Billing Source</Label>
            <Select value={f.billing_source} onValueChange={v => set('billing_source', v)}>
              <SelectTrigger data-testid="select-line-billing"><SelectValue /></SelectTrigger>
              <SelectContent>{BILLING_SOURCES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Remarks</Label><Textarea value={f.remarks} onChange={e => set('remarks', e.target.value)} data-testid="input-line-remarks" /></div>
          <div className="col-span-2 bg-muted/40 rounded p-2 text-xs" data-testid="line-calc-preview">
            Base = <b>{base.toFixed(2)}</b> · Taxable = <b>{taxable.toFixed(2)}</b> · GST = <b>{gst.toFixed(2)}</b> · <span className="text-primary font-semibold">Final = {finalAmt.toFixed(2)}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(f)} data-testid="btn-save-line">Save Line</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────  DETAIL SHELL  ─────────────────────────── */
function OnboardingDetail({ call, token, oid, onBack }) {
  const { session } = useAuth();
  const isSA = !!session?.is_super_admin;
  const [showBankMaster, setShowBankMaster] = useState(false);
  const [ob, setOb] = useState(null);
  const [heads, setHeads] = useState([]);
  const [lines, setLines] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter241 — controlled tab so reload() preserves the tab.
  const [activeTab, setActiveTab] = useState('setup');  // iter249 — default to new Franchise Setup
  const [pendingSetup, setPendingSetup] = useState(null); // iter256 — auto-open setup modal from Dashboard [Open]
  // iter248 — Category → PO/Invoice wizard preset (from Franchise Configuration)
  const [presetPOCategory, setPresetPOCategory] = useState(null);
  const [presetInvoiceCategory, setPresetInvoiceCategory] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d] = await Promise.all([
        call(`/get/${oid}`, {}), call(`/dashboard/${oid}`, {}),
      ]);
      setOb(g.onboarding); setHeads(g.heads); setLines(g.lines); setDash(d);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [oid]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !ob) return <div className="text-sm text-muted-foreground p-8">Loading onboarding…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={onBack} data-testid="btn-back-list"><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
          <div className="text-xs font-mono text-muted-foreground mt-1">{oid}</div>
          <div className="text-lg font-semibold">{ob.franchise_code} — {ob.franchise_name}</div>
        </div>
        <Badge variant="outline" data-testid="onboarding-status-badge">{ob.status}</Badge>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 max-w-xl">
          <TabsTrigger value="setup" data-testid="tab-setup">Franchise Setup</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="phase234" data-testid="tab-phase234">Documents (PO/Invoice/Go-Live)</TabsTrigger>
        </TabsList>
        <TabsContent value="setup">
          <FranchiseSetupTab token={token} oid={oid} ob={ob} isSA={isSA} onReload={reload} />
        </TabsContent>
        <TabsContent value="dashboard">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><DashboardTile dash={dash} onOpenSection={(k) => {
              // iter254 — Route section clicks to the tab that owns
              // the actual data.  iter256 — auto-open the SectionSetup
              // modal for the 6 auto-computed sections.
              if (k === 'commercial_setup') setActiveTab('setup');
              else {
                setActiveTab('phase234');
                if (['staff_mapping','pos_mapping','aggregator_mapping','compliance','premises_condition','pre_opening_tasks'].includes(k)) {
                  setPendingSetup(k);
                }
              }
            }} /></div>
            <MFPLBankAssignedCard call={call} token={token} oid={oid} isSA={isSA} onOpenMaster={() => setShowBankMaster(true)} />
          </div>
        </TabsContent>
        <TabsContent value="phase234">
          <Phase234Panel call={call} token={token} oid={oid} onReload={reload}
            lines={lines} heads={heads} onboarding={ob} isSA={isSA}
            presetPOCategory={presetPOCategory}
            presetInvoiceCategory={presetInvoiceCategory}
            pendingSetup={pendingSetup}
            onPendingSetupConsumed={() => setPendingSetup(null)}
            onPresetConsumed={() => { setPresetPOCategory(null); setPresetInvoiceCategory(null); }} />
        </TabsContent>
      </Tabs>
      <MFPLBankMasterModal open={showBankMaster} onClose={() => setShowBankMaster(false)} token={token} isSA={isSA} />
    </div>
  );
}

/* ─────────────────────────  PO / INVOICE PICKER MODAL  ─────────────────────────── */
// Shared modal for creating a PO or an Invoice.  Shows every commercial
// line grouped by head as a checkbox picker; each row lets the user
// override quantity / rate / discount / GST before submission.  An
// "Add ad-hoc line" row supports items not in the commercial setup.
function DocPickerModal({ call, token, oid, mode, onClose, onCreated, heads, lines, onboarding, poRefOptions }) {
  const cur = onboarding?.currency_code || 'INR';
  const [selected, setSelected] = useState({}); // {commercial_line_id: {qty, rate, disc, gst}}
  const [adhoc, setAdhoc] = useState([]);
  const [vendor, setVendor] = useState('');
  const [vendorGSTIN, setVendorGSTIN] = useState('');
  const [vendorAddr, setVendorAddr] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [terms, setTerms] = useState('Payment 30 days from invoice');
  const [invoiceType, setInvoiceType] = useState('FRANCHISE_FEE');
  const [linkedPO, setLinkedPO] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // iter241 — Catalog picker (category → subcategory → items).
  const [catTree, setCatTree] = useState([]);
  const [catCategory, setCatCategory] = useState('');
  const [catSubcategory, setCatSubcategory] = useState('');
  const [catItems, setCatItems] = useState([]);
  // iter242 — inline qty override in the catalog table before "Add"
  const [catQtyOverride, setCatQtyOverride] = useState({}); // {item_id: qty}
  // Preview modal after successful create — user reviews then downloads PDF.
  const [preview, setPreview] = useState(null); // {kind: 'PO'|'INVOICE', doc, lines}
  // iter242 — Email PDF prompt state
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', sending: false });
  useEffect(() => {
    call('/catalog/categories', {}).then(r => setCatTree(r.tree || [])).catch(() => { });
  }, [call]);
  useEffect(() => {
    if (!catCategory) { setCatItems([]); return; }
    call('/catalog/list', { category: catCategory, subcategory: catSubcategory }).then(r => setCatItems(r.items || [])).catch(() => { });
  }, [call, catCategory, catSubcategory]);
  const addCatItem = (it, overrideQty) => {
    const qty = (overrideQty !== undefined && overrideQty !== null && overrideQty !== '')
      ? +overrideQty
      : (catQtyOverride[it.id] !== undefined && catQtyOverride[it.id] !== '')
        ? +catQtyOverride[it.id]
        : (it.default_qty || 1);
    setAdhoc(a => [...a, {
      description: it.description, item_code: it.item_code,
      quantity: qty, unit: it.unit || 'NO',
      rate: it.default_rate || 0, discount: 0,
      gst_percent: it.gst_percent || 0, hsn_code: it.hsn_code || '',
      billing_source: 'MFPL', classification: 'ASSET',
      supplier: it.default_supplier || '',
    }]);
  };
  // iter242 — bulk add every item currently visible in the catalog
  //   table (respects any Category+Subcategory filter) using the
  //   excel-defined default_qty for each row.
  const addAllCatItems = () => {
    if (!catItems.length) { toast.error('No items to add'); return; }
    catItems.forEach(it => addCatItem(it, it.default_qty));
    toast.success(`Added ${catItems.length} item(s) with excel default qty — edit below`);
  };

  const isPO = mode === 'PO';
  const linesByHead = React.useMemo(() => {
    const m = {}; (lines || []).forEach(l => { (m[l.head_id] = m[l.head_id] || []).push(l); });
    return m;
  }, [lines]);

  const toggleLine = (l) => setSelected(prev => {
    const next = { ...prev };
    if (next[l.id]) delete next[l.id];
    else next[l.id] = { qty: l.quantity, rate: l.rate, disc: l.discount || 0, gst: l.gst_percent || 0, description: l.description };
    return next;
  });
  const updateSel = (id, k, v) => setSelected(p => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const addAdhoc = () => setAdhoc(a => [...a, { description: '', quantity: 1, unit: 'LS', rate: 0, discount: 0, gst_percent: 18, hsn_code: '', billing_source: 'MFPL', classification: 'OTHER' }]);
  const setAdhocField = (i, k, v) => setAdhoc(a => a.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmAdhoc = (i) => setAdhoc(a => a.filter((_, idx) => idx !== i));

  const _line_total = (qty, rate, disc, gst) => {
    const base = (+qty || 0) * (+rate || 0);
    const taxable = Math.max(base - (+disc || 0), 0);
    return taxable + taxable * (+gst || 0) / 100;
  };
  const grandTotal = React.useMemo(() => {
    let t = 0;
    Object.values(selected).forEach(s => t += _line_total(s.qty, s.rate, s.disc, s.gst));
    adhoc.forEach(a => t += _line_total(a.quantity, a.rate, a.discount, a.gst_percent));
    return t;
  }, [selected, adhoc]);

  const submit = async () => {
    const line_ids = Object.keys(selected);
    const additional_lines = [
      ...Object.entries(selected).map(([cid, s]) => ({ commercial_line_id: cid, quantity: +s.qty, rate: +s.rate, discount: +s.disc, gst_percent: +s.gst, description: s.description })),
      ...adhoc,
    ];
    if (line_ids.length === 0 && adhoc.length === 0) { toast.error('Pick at least one line'); return; }
    setSubmitting(true);
    try {
      if (isPO) {
        if (!vendor) { toast.error('Vendor name is required'); setSubmitting(false); return; }
        const r = await call('/po/create', {
          onboarding_id: oid, vendor, vendor_gstin: vendorGSTIN, vendor_address: vendorAddr,
          expected_delivery: expectedDelivery, terms, remarks,
          line_ids, additional_lines
        });
        toast.success(`PO ${r.po.po_number} created — review preview below`);
        setPreview({ kind: 'PO', doc: r.po, lines: r.lines });
      } else {
        const r = await call('/invoice/create', {
          onboarding_id: oid, po_id: linkedPO || null,
          invoice_type: invoiceType, due_date: dueDate, remarks,
          line_ids, additional_lines
        });
        toast.success(`Invoice ${r.invoice.invoice_number} created — review preview below`);
        setPreview({ kind: 'INVOICE', doc: r.invoice, lines: r.lines });
      }
      // NOTE: onCreated() is deliberately NOT called here — we keep
      // the modal open showing the preview.  When the user hits
      // "Done" on the preview we call onCreated to refresh the list
      // but keep the parent tab where the user was.
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const downloadDocPDF = async () => {
    if (!preview) return;
    const path = preview.kind === 'PO'
      ? `/po/pdf/${preview.doc.id}`
      : `/invoice/pdf/${preview.doc.id}`;
    const filename = preview.kind === 'PO'
      ? `PO_${preview.doc.po_number.replace(/\//g, '-')}.pdf`
      : `Invoice_${preview.doc.invoice_number.replace(/\//g, '-')}.pdf`;
    try {
      const res = await fetch(`${API}/api/franchise-onboarding${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded and auto-attached to Franchise Documents');
    } catch (e) { toast.error(e.message); }
  };

  // iter242 — Email the generated PDF to vendor (PO) or franchisee (Invoice)
  const emailDocPDF = async () => {
    if (!preview) return;
    if (!emailForm.to || !emailForm.to.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    const path = preview.kind === 'PO'
      ? `/po/email/${preview.doc.id}`
      : `/invoice/email/${preview.doc.id}`;
    setEmailForm(f => ({ ...f, sending: true }));
    try {
      const r = await call(path, {
        to_email: emailForm.to.trim(),
        cc: (emailForm.cc || '').trim() || null,
      });
      toast.success(`Email sent to ${r.to}${r.cc ? ` (cc ${r.cc})` : ''}`);
      setEmailForm({ to: '', cc: '', sending: false });
    } catch (e) {
      toast.error(e.message);
      setEmailForm(f => ({ ...f, sending: false }));
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid={`${mode.toLowerCase()}-picker-modal`}>
        <DialogHeader><DialogTitle>Create {isPO ? 'Purchase Order' : 'MFPL Invoice'}</DialogTitle></DialogHeader>

        {/* Header fields */}
        <div className="grid grid-cols-3 gap-3 border-b pb-3">
          {isPO ? (
            <>
              <div><Label>Vendor *</Label><Input value={vendor} onChange={e => setVendor(e.target.value)} data-testid="po-vendor" /></div>
              <div><Label>Vendor GSTIN</Label><Input value={vendorGSTIN} onChange={e => setVendorGSTIN(e.target.value.toUpperCase())} data-testid="po-gstin" /></div>
              <div><Label>Expected Delivery</Label><Input type="date" value={expectedDelivery} onChange={e => setExpectedDelivery(e.target.value)} data-testid="po-eta" /></div>
              <div className="col-span-2"><Label>Vendor Address</Label><Input value={vendorAddr} onChange={e => setVendorAddr(e.target.value)} data-testid="po-vaddr" /></div>
              <div><Label>Terms</Label><Input value={terms} onChange={e => setTerms(e.target.value)} data-testid="po-terms" /></div>
            </>
          ) : (
            <>
              <div><Label>Invoice Type</Label>
                <Select value={invoiceType} onValueChange={setInvoiceType}>
                  <SelectTrigger data-testid="inv-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['FRANCHISE_FEE', 'BRAND_PRODUCT', 'EQUIPMENT', 'UTENSILS', 'UNIFORM', 'MENU_PRINTING', 'PACKAGING', 'OPENING_INVENTORY', 'TRAINING', 'TECHNOLOGY', 'MARKETING', 'TRAVEL', 'OTHER'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Linked PO (optional)</Label>
                <Select value={linkedPO || 'NONE'} onValueChange={v => setLinkedPO(v === 'NONE' ? '' : v)}>
                  <SelectTrigger data-testid="inv-po-ref"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">-- No PO reference --</SelectItem>
                    {(poRefOptions || []).map(p => <SelectItem key={p.id} value={p.id}>{p.po_number} — {p.vendor}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Due Date</Label><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="inv-due" /></div>
            </>
          )}
          <div className="col-span-3"><Label>Remarks</Label><Textarea value={remarks} onChange={e => setRemarks(e.target.value)} data-testid={`${mode.toLowerCase()}-remarks`} /></div>
        </div>

        {/* Line picker grouped by head */}
        <div className="pt-3 space-y-3">
          <div className="text-sm font-semibold">Pick lines from Commercial Setup (edit qty/rate/GST inline)</div>
          {heads.map(h => {
            const rows = linesByHead[h.id] || [];
            if (rows.length === 0) return null;
            return (
              <div key={h.id} className="border rounded" data-testid={`picker-head-${h.id}`}>
                <div className="bg-muted/40 px-2 py-1 text-xs font-semibold">{h.name} <Badge variant="outline" className="text-[10px] ml-1">{h.classification}</Badge></div>
                <table className="w-full text-xs">
                  <thead className="text-left"><tr className="border-b"><th className="w-8"></th><th>Description</th><th className="w-16">Qty</th><th className="w-20">Rate</th><th className="w-20">Discount</th><th className="w-16">GST%</th><th className="w-24 text-right">Line Total</th></tr></thead>
                  <tbody>
                    {rows.map(l => {
                      const sel = selected[l.id];
                      const val = sel || { qty: l.quantity, rate: l.rate, disc: l.discount || 0, gst: l.gst_percent || 0 };
                      const total = _line_total(val.qty, val.rate, val.disc, val.gst);
                      return (
                        <tr key={l.id} className={`border-b ${sel ? 'bg-primary/5' : ''}`} data-testid={`picker-row-${l.id}`}>
                          <td className="p-1"><input type="checkbox" checked={!!sel} onChange={() => toggleLine(l)} data-testid={`chk-line-${l.id}`} /></td>
                          <td className="p-1">{l.description}</td>
                          <td><Input className="h-7 text-xs" type="number" value={val.qty} onChange={e => sel && updateSel(l.id, 'qty', +e.target.value)} disabled={!sel} data-testid={`qty-${l.id}`} /></td>
                          <td><Input className="h-7 text-xs" type="number" value={val.rate} onChange={e => sel && updateSel(l.id, 'rate', +e.target.value)} disabled={!sel} /></td>
                          <td><Input className="h-7 text-xs" type="number" value={val.disc} onChange={e => sel && updateSel(l.id, 'disc', +e.target.value)} disabled={!sel} /></td>
                          <td><Input className="h-7 text-xs" type="number" value={val.gst} onChange={e => sel && updateSel(l.id, 'gst', +e.target.value)} disabled={!sel} /></td>
                          <td className="text-right pr-2">{sel ? currency(total, cur) : <span className="text-muted-foreground">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Ad-hoc rows + Catalog picker */}
          <div className="border rounded" data-testid="catalog-picker">
            <div className="bg-muted/40 px-2 py-1 text-xs font-semibold flex items-center justify-between">
              <span>Pre-Approved Catalog — Category / Subcategory</span>
              {catItems.length > 0 && (
                <Button size="sm" variant="outline" className="h-6 text-[10px]"
                  onClick={addAllCatItems}
                  data-testid="btn-add-all-catalog">
                  <Plus className="w-3 h-3 mr-1" />Add all {catItems.length} with excel qty
                </Button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 p-2">
              <Select value={catCategory || 'NONE'} onValueChange={v => { setCatCategory(v === 'NONE' ? '' : v); setCatSubcategory(''); }}>
                <SelectTrigger data-testid="cat-category"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">-- Select category --</SelectItem>
                  {catTree.map(c => <SelectItem key={c.category} value={c.category}>{c.category.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={catSubcategory || 'ALL'} onValueChange={v => setCatSubcategory(v === 'ALL' ? '' : v)} disabled={!catCategory}>
                <SelectTrigger data-testid="cat-subcategory"><SelectValue placeholder="Subcategory" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">-- All subcategories --</SelectItem>
                  {(catTree.find(c => c.category === catCategory)?.subcategories || []).map(s => <SelectItem key={s.subcategory} value={s.subcategory}>{s.subcategory || '(none)'} ({s.item_count})</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground self-center">Edit qty inline, then click <b>+</b> — or use "Add all" above.</div>
            </div>
            {catItems.length > 0 && (
              <div className="max-h-40 overflow-y-auto border-t">
                <table className="w-full text-xs">
                  <thead><tr className="border-b bg-muted/20"><th className="p-1 text-left">Code</th><th className="text-left">Description</th><th className="w-16">Qty</th><th>Unit</th><th>Rate</th><th>GST</th><th>HSN</th><th></th></tr></thead>
                  <tbody>
                    {catItems.map(it => (
                      <tr key={it.id} className="border-b hover:bg-muted/10" data-testid={`cat-item-${it.item_code}`}>
                        <td className="p-1 font-mono">{it.item_code}</td>
                        <td>{it.description}</td>
                        <td className="text-center">
                          <Input
                            type="number"
                            className="h-6 text-xs w-14 px-1"
                            value={catQtyOverride[it.id] !== undefined ? catQtyOverride[it.id] : it.default_qty}
                            onChange={e => setCatQtyOverride(prev => ({ ...prev, [it.id]: e.target.value }))}
                            data-testid={`cat-qty-${it.item_code}`}
                          />
                        </td>
                        <td className="text-center">{it.unit}</td>
                        <td className="text-right">{currency(it.default_rate, cur)}</td>
                        <td className="text-center">{it.gst_percent}%</td>
                        <td className="text-center">{it.hsn_code || '—'}</td>
                        <td><Button size="sm" variant="ghost" onClick={() => { addCatItem(it); toast.success(`${it.description} added`); }} data-testid={`btn-add-cat-${it.item_code}`}><Plus className="w-3 h-3" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="border rounded" data-testid="adhoc-block">
            <div className="bg-muted/40 px-2 py-1 flex items-center justify-between">
              <span className="text-xs font-semibold">Ad-hoc / Additional Lines (not in commercial setup)</span>
              <Button size="sm" variant="ghost" onClick={addAdhoc} data-testid="btn-add-adhoc"><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>
            {adhoc.length === 0 ? <div className="p-2 text-xs text-muted-foreground">Click "Add" to include items not part of the pre-populated commercial setup.</div> : (
              <table className="w-full text-xs">
                <thead><tr className="border-b"><th className="p-1">Description</th><th className="w-16">Qty</th><th className="w-16">Unit</th><th className="w-20">Rate</th><th className="w-20">Disc</th><th className="w-16">GST%</th><th className="w-24 text-right">Total</th><th className="w-8"></th></tr></thead>
                <tbody>
                  {adhoc.map((r, i) => (
                    <tr key={i} className="border-b" data-testid={`adhoc-row-${i}`}>
                      <td><Input className="h-7 text-xs" value={r.description} onChange={e => setAdhocField(i, 'description', e.target.value)} placeholder="Item description" /></td>
                      <td><Input className="h-7 text-xs" type="number" value={r.quantity} onChange={e => setAdhocField(i, 'quantity', +e.target.value)} /></td>
                      <td><Input className="h-7 text-xs" value={r.unit} onChange={e => setAdhocField(i, 'unit', e.target.value)} /></td>
                      <td><Input className="h-7 text-xs" type="number" value={r.rate} onChange={e => setAdhocField(i, 'rate', +e.target.value)} /></td>
                      <td><Input className="h-7 text-xs" type="number" value={r.discount} onChange={e => setAdhocField(i, 'discount', +e.target.value)} /></td>
                      <td><Input className="h-7 text-xs" type="number" value={r.gst_percent} onChange={e => setAdhocField(i, 'gst_percent', +e.target.value)} /></td>
                      <td className="text-right pr-2">{currency(_line_total(r.quantity, r.rate, r.discount, r.gst_percent), cur)}</td>
                      <td><Button size="sm" variant="ghost" onClick={() => rmAdhoc(i)}><Trash2 className="w-3 h-3" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <DialogFooter className="border-t pt-3 mt-3">
          {preview ? (
            <div className="w-full space-y-3" data-testid="doc-preview-panel">
              <div className="text-sm font-semibold text-primary">
                ✓ {preview.kind === 'PO' ? 'PO' : 'Invoice'} <span className="font-mono">{preview.kind === 'PO' ? preview.doc.po_number : preview.doc.invoice_number}</span> created.
              </div>
              <div className="max-h-56 overflow-y-auto border rounded">
                <table className="w-full text-xs"><thead className="text-left bg-muted/20"><tr><th className="p-1">#</th><th>Description</th><th>Qty</th><th>Rate</th><th>GST</th><th className="text-right">Total</th></tr></thead>
                  <tbody>{preview.lines.map((l, i) => (
                    <tr key={l.id} className="border-b"><td className="p-1">{i + 1}</td><td>{l.description}</td><td>{l.quantity}</td><td>{currency(l.rate, cur)}</td><td>{l.gst_percent}%</td><td className="text-right font-semibold">{currency(l.final_amount, cur)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="text-sm text-right">
                Grand Total: <b>{currency(preview.doc.totals?.final_amount, cur)}</b>
              </div>
              {/* iter242 — Email PDF panel */}
              <div className="border rounded p-2 bg-slate-50 space-y-2" data-testid="email-panel">
                <div className="text-xs font-semibold">
                  Email this {preview.kind === 'PO' ? 'PO to the Vendor' : 'Invoice to the Franchise Owner'}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder={preview.kind === 'PO' ? 'vendor@example.com' : 'franchisee@example.com'}
                    value={emailForm.to}
                    onChange={e => setEmailForm(f => ({ ...f, to: e.target.value }))}
                    data-testid="input-email-to"
                  />
                  <Input
                    placeholder="CC (optional)"
                    value={emailForm.cc}
                    onChange={e => setEmailForm(f => ({ ...f, cc: e.target.value }))}
                    data-testid="input-email-cc"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={emailDocPDF}
                    disabled={emailForm.sending || !emailForm.to}
                    data-testid="btn-email-doc-pdf"
                  >
                    {emailForm.sending ? 'Sending…' : `Email PDF`}
                  </Button>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={downloadDocPDF} data-testid="btn-preview-download-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
                <Button onClick={() => { onCreated(); }} data-testid="btn-preview-done">Done</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 text-sm">Grand Total: <b>{currency(grandTotal, cur)}</b> — {Object.keys(selected).length + adhoc.length} line(s)</div>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid={`btn-submit-${mode.toLowerCase()}`}>{submitting ? 'Saving…' : `Preview ${isPO ? 'PO' : 'Invoice'}`}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────  PHASE 2 / 3 / 4 PANEL  ─────────────────────────── */
function Phase234Panel({ call, token, oid, onReload, lines, heads, onboarding, isSA, presetPOCategory, presetInvoiceCategory, onPresetConsumed, pendingSetup, onPendingSetupConsumed }) {
  const [pos, setPOs] = useState([]);
  const [invs, setInvs] = useState([]);
  const [wc, setWC] = useState({});
  const [docs, setDocs] = useState([]);
  const [pickerMode, setPickerMode] = useState(null); // 'PO' | 'INVOICE' | null
  const [showPOWizard, setShowPOWizard] = useState(false); // iter243
  const [wizardPreset, setWizardPreset] = useState(null); // iter248 — {po_category_code, name, lines[]}
  const [invoiceWizard, setInvoiceWizard] = useState(null); // iter248 — {invoice_category_code, name, lines[]}
  const [poTab, setPoTab] = useState('active'); // iter243 — active|archive
  const [archivedPOs, setArchivedPOs] = useState([]);
  const [showDanger, setShowDanger] = useState(false); // iter243
  const [dangerImpact, setDangerImpact] = useState(null);
  const [dangerForm, setDangerForm] = useState({ reason: '', typed: '', acknowledged: false });
  // iter251 — Selected master snapshots + editor/wizard state (moved
  // here from Franchise Setup tab per the "SETUP = selection only,
  // DOCUMENTS = execution" split).
  const [poSnapshots, setPoSnapshots] = useState([]);
  const [invSnapshots, setInvSnapshots] = useState([]);
  const [snapshotEditor, setSnapshotEditor] = useState(null); // {snapshot}
  const [invoiceMasterWizard, setInvoiceMasterWizard] = useState(null); // {snapshot} for master-mode invoice wizard
  const [readiness, setReadiness] = useState(null); // iter253 — Go-Live readiness engine payload
  const [showInteriorAsset, setShowInteriorAsset] = useState(false); // iter255 (C1)
  const [setupSection, setSetupSection] = useState(null); // iter256 — section-setup modal key

  // iter256 — Auto-open the SectionSetup modal when the Dashboard
  // [Open] button routes to this tab with a specific section key.
  useEffect(() => {
    if (pendingSetup) {
      setSetupSection(pendingSetup);
      onPendingSetupConsumed && onPendingSetupConsumed();
    }
  }, [pendingSetup, onPendingSetupConsumed]);
  const cur = onboarding?.currency_code || 'INR';

  // iter248 — Handle preset PO/Invoice category from Franchise Configuration tab
  useEffect(() => {
    if (presetPOCategory && !showPOWizard) {
      (async () => {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config-master/franchise/category-lines/${oid}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, kind: 'PO', category_code: presetPOCategory.code }),
          }).then(r => r.json());
          setWizardPreset({
            po_category_code: presetPOCategory.code,
            po_category_name: presetPOCategory.name,
            lines: r.lines || [],
          });
          setShowPOWizard(true);
        } catch (e) { toast.error(e.message); }
        finally { onPresetConsumed?.(); }
      })();
    }
  }, [presetPOCategory, showPOWizard, oid, token, onPresetConsumed]);

  useEffect(() => {
    if (presetInvoiceCategory && !invoiceWizard) {
      (async () => {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config-master/franchise/category-lines/${oid}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, kind: 'INVOICE', category_code: presetInvoiceCategory.code }),
          }).then(r => r.json());
          setInvoiceWizard({
            invoice_category_code: presetInvoiceCategory.code,
            invoice_category_name: presetInvoiceCategory.name,
            lines: r.lines || [],
          });
        } catch (e) { toast.error(e.message); }
        finally { onPresetConsumed?.(); }
      })();
    }
  }, [presetInvoiceCategory, invoiceWizard, oid, token, onPresetConsumed]);

  const loadAll = useCallback(async () => {
    try {
      const [p, i, w, d, m, r] = await Promise.all([
        call('/po/list', { onboarding_id: oid }),
        call('/invoice/list', { onboarding_id: oid }),
        call(`/working-capital/get/${oid}`, {}),
        call('/documents/list', { onboarding_id: oid }),
        // iter251 — masters selected for this franchise
        fetch(`${API}/api/masters/franchise/list/${oid}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        }).then(r => r.json()).catch(() => ({ po_snapshots: [], invoice_snapshots: [] })),
        // iter253 — Go-Live readiness state
        call(`/readiness/${oid}`, {}).catch(() => null),
      ]);
      setPOs(p.items); setInvs(i.items); setWC(w.working_capital || {}); setDocs(d.items);
      setPoSnapshots(m.po_snapshots || []);
      setInvSnapshots(m.invoice_snapshots || []);
      setReadiness(r);
    } catch (e) { toast.error(e.message); }
  }, [call, oid, token]);
  const loadArchive = useCallback(async () => {
    try {
      const r = await call('/po/archive/list', { onboarding_id: oid });
      setArchivedPOs(r.items);
    } catch (e) { toast.error(e.message); }
  }, [call, oid]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (poTab === 'archive') loadArchive(); }, [poTab, loadArchive]);

  const approvePO = async (po_id) => {
    try { await call('/po/approve', { po_id }); toast.success('PO approved'); loadAll(); }
    catch (e) {
      // iter243 — surface RATE_REQUIRED message with SA override option
      if (isSA && e.message.includes('no Rate')) {
        if (window.confirm(`${e.message}\n\nOverride as SA and approve anyway?`)) {
          try { await call('/po/approve', { po_id, allow_zero_rate: true }); toast.success('PO approved (SA override)'); loadAll(); }
          catch (e2) { toast.error(e2.message); }
        }
      } else { toast.error(e.message); }
    }
  };
  const deletePO = async (po_id, po_num) => {
    if (!window.confirm(`Permanently remove PO ${po_num} from active onboarding records?\n\nThe record will remain available only in Archive / Audit History.\nThe PO number will NOT be reused.`)) return;
    const reason = window.prompt(`Enter deletion reason for ${po_num}:`, '');
    if (reason === null || !reason.trim()) return;
    try { await call(`/po/delete/${po_id}`, { reason }); toast.success(`PO ${po_num} archived`); loadAll(); } catch (e) { toast.error(e.message); }
  };
  const approveInvoice = async (invoice_id) => { try { await call('/invoice/approve', { invoice_id }); toast.success('Invoice approved & locked'); loadAll(); } catch (e) { toast.error(e.message); } };
  const deleteInvoice = async (invoice_id, inv_num) => {
    const reason = window.prompt(`Cancel invoice ${inv_num}? (Not allowed if payments exist — issue Credit Note instead.) Enter reason:`, '');
    if (reason === null) return;
    try { await call(`/invoice/delete/${invoice_id}`, { reason }); toast.success(`Invoice ${inv_num} cancelled`); loadAll(); } catch (e) { toast.error(e.message); }
  };
  const recordPayment = async (inv) => {
    const amt = parseFloat(prompt(`Payment amount (balance = ${inv.balance_amount})?`, inv.balance_amount));
    if (!amt) return;
    try {
      const r = await call('/payment/record', { onboarding_id: oid, invoice_id: inv.id, amount: amt, payment_date: new Date().toISOString().slice(0, 10), mode: 'NEFT' });
      toast.success(`Receipt ${r.payment.receipt_number} · Status ${r.new_status}`);
      loadAll();
    } catch (e) { toast.error(e.message); }
  };
  const upsertWC = async () => {
    const required = parseFloat(prompt('Required working capital?', wc.required_amount || onboarding.initial_working_capital || 0));
    const received = parseFloat(prompt('Received amount?', wc.received_amount || 0));
    if (isNaN(required)) return;
    try {
      await call('/working-capital/upsert', { onboarding_id: oid, required_amount: required, received_amount: received || 0, received_date: new Date().toISOString().slice(0, 10) });
      toast.success('Working capital updated'); loadAll();
    } catch (e) { toast.error(e.message); }
  };
  const freezeAssets = async () => {
    if (!window.confirm('Freeze the Opening Asset Register? This creates the immutable baseline.')) return;
    try { const r = await call(`/asset-opening/finalize/${oid}`, { onboarding_id: oid, reason: 'Initial finalisation' }); toast.success(`Assets frozen — ${r.snapshot.total_lines} lines`); onReload(); }
    catch (e) { toast.error(e.message); }
  };
  const freezeInventory = async () => {
    if (!window.confirm('Freeze the Opening Inventory? This becomes Day-1 stock.')) return;
    try { const r = await call(`/inventory-opening/finalize/${oid}`, { onboarding_id: oid, reason: 'Initial finalisation' }); toast.success(`Inventory frozen — ${r.snapshot.total_lines} lines`); onReload(); }
    catch (e) { toast.error(e.message); }
  };
  const markSectionDone = async (section) => {
    try { await call('/mark-section-done', { onboarding_id: oid, section }); toast.success(`${section} marked DONE`); onReload(); }
    catch (e) { toast.error(e.message); }
  };
  const confirmGoLive = async () => {
    const dt = prompt('Operational Go-Live Date (YYYY-MM-DD)?', new Date().toISOString().slice(0, 10));
    if (!dt) return;
    const override = window.confirm('Override any remaining mandatory blockers? (Super Admin only)');
    try {
      const r = await call(`/go-live/confirm/${oid}`, { operational_go_live_date: dt, override_blockers: override, reason: 'Go-Live confirmed' });
      toast.success(`Centre LIVE from ${r.go_live.operational_go_live_date}`);
      onReload();
    } catch (e) { toast.error(e.message); }
  };

  // iter261 — Activate the centre for operations (LIVE → operational_active=true)
  const activateCentre = async () => {
    if (!window.confirm(
      'Activating this centre will make it available across Login, ' +
      'Sales, Attendance, Salary, Inventory, Accounts, Booking and other ' +
      'operational modules.\n\nProceed?'
    )) return;
    try {
      const r = await call('/activate-centre-for-operations',
        { onboarding_id: oid, confirm: true,
          reason: 'Post go-live centre activation' });
      toast.success(`Centre ${r.code} is now OPERATIONAL.`);
      onReload();
    } catch (e) { toast.error(e.message); }
  };
  const deactivateCentre = async () => {
    const reason = prompt('Reason for deactivation? (required)');
    if (!reason || !reason.trim()) return;
    if (!window.confirm(
      'Deactivating will HIDE this centre from all operational dropdowns ' +
      '(Login/Sales/Attendance/Salary/Inventory/etc.) without deleting ' +
      'its data. Proceed?'
    )) return;
    try {
      const r = await call('/deactivate-centre-for-operations',
        { onboarding_id: oid, confirm: true, reason: reason.trim() });
      toast.success(`Centre ${r.code} deactivated (hidden from operations).`);
      onReload();
    } catch (e) { toast.error(e.message); }
  };

  const downloadPDF = async (path, filename) => {
    try {
      const res = await fetch(`${API}/api/franchise-onboarding${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || 'Failed'); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  // iter242 — Email a saved PO / Invoice PDF
  const emailDoc = async (kind, id, num) => {
    const to = window.prompt(
      `Email ${kind} ${num}\n\nRecipient email:`, '');
    if (to === null) return;
    if (!to || !to.includes('@')) { toast.error('Enter a valid email'); return; }
    const cc = window.prompt('CC (optional, leave blank to skip):', '') || '';
    try {
      const path = kind === 'PO' ? `/po/email/${id}` : `/invoice/email/${id}`;
      const r = await call(path, {
        to_email: to.trim(),
        cc: (cc || '').trim() || null,
      });
      toast.success(`Emailed to ${r.to}${r.cc ? ` (cc ${r.cc})` : ''}`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4" data-testid="phase234-panel">
      {/* PO */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Purchase Orders (Phase 2)</CardTitle>
          <div className="flex items-center gap-2">
            <Tabs value={poTab} onValueChange={setPoTab}>
              <TabsList className="h-8">
                <TabsTrigger value="active" className="text-xs h-6 px-2" data-testid="po-tab-active">Active</TabsTrigger>
                <TabsTrigger value="archive" className="text-xs h-6 px-2" data-testid="po-tab-archive">Archive ({archivedPOs.length})</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button size="sm" onClick={() => { setWizardPreset(null); setShowPOWizard(true); }} data-testid="btn-create-po"><Plus className="w-4 h-4 mr-1" />Create PO</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* iter252 — Selected PO Masters: compact name-only list (no cards / no buttons / no counts). */}
          {poSnapshots.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2 text-muted-foreground">Selected PO Masters for this Franchise</div>
              <div className="flex flex-wrap gap-1.5" data-testid="po-master-list">
                {poSnapshots.map(s => (
                  <span
                    key={s.id}
                    className="inline-flex items-center px-2 py-1 rounded-full border bg-muted/20 text-xs font-medium"
                    data-testid={`po-master-chip-${s.worksheet_name.replace(/\s+/g, '-')}`}
                  >{s.worksheet_name}</span>
                ))}
              </div>
            </div>
          )}
          {poSnapshots.length === 0 && (
            <div className="border border-dashed rounded p-3 text-xs text-muted-foreground text-center">
              No PO Masters selected. Go to <b>Franchise Setup</b> tab and pick the masters that apply to this franchise.
            </div>
          )}
          {poTab === 'active' ? (
            pos.filter(p => !p.archived).length === 0
              ? <div className="text-xs text-muted-foreground">No active POs. Click Create PO to start the 4-step wizard driven by the Purnabramha Excel catalogue.</div>
              : (
            <table className="w-full text-xs"><thead className="text-left border-b"><tr><th className="p-1">PO #</th><th>Vendor</th><th>Category</th><th>Total</th><th>Status</th><th className="w-52">Actions</th></tr></thead><tbody>
              {pos.filter(p => !p.archived).map(p => (
                <tr key={p.id} className="border-b" data-testid={`po-row-${p.po_number}`}>
                  <td className="p-1 font-mono">{p.po_number}</td>
                  <td>{p.vendor}</td>
                  <td className="text-muted-foreground">{p.po_category || '—'}</td>
                  <td>{currency(p.totals?.final_amount, cur)}</td>
                  <td><Badge variant="outline" className="text-[10px]">{p.status}</Badge></td>
                  <td className="flex gap-1 flex-wrap">
                    {p.status === 'DRAFT' && <Button size="sm" variant="ghost" onClick={() => approvePO(p.id)} data-testid={`btn-approve-po-${p.po_number}`}>Approve</Button>}
                    <Button size="sm" variant="outline" onClick={() => downloadPDF(`/po/pdf/${p.id}`, `PO_${p.po_number.replace(/\//g, '-')}.pdf`)} data-testid={`btn-dl-po-${p.po_number}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => emailDoc('PO', p.id, p.po_number)} data-testid={`btn-email-po-${p.po_number}`}><Mail className="w-3 h-3 mr-1" />Email</Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePO(p.id, p.po_number)} data-testid={`btn-del-po-${p.po_number}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody></table>
              )
          ) : (
            archivedPOs.length === 0
              ? <div className="text-xs text-muted-foreground">No archived POs.</div>
              : (
            <table className="w-full text-xs" data-testid="po-archive-table"><thead className="text-left border-b"><tr><th className="p-1">PO #</th><th>Vendor</th><th>Total</th><th>Original Status</th><th>Archived At</th><th>Archived By</th><th>Reason</th></tr></thead><tbody>
              {archivedPOs.map(p => (
                <tr key={p.id} className="border-b bg-red-50/30" data-testid={`po-archive-row-${p.po_number}`}>
                  <td className="p-1 font-mono">{p.po_number}</td>
                  <td>{p.vendor}</td>
                  <td>{currency(p.totals?.final_amount, cur)}</td>
                  <td><Badge variant="outline" className="text-[10px]">{p.original_status || 'ARCHIVED'}</Badge></td>
                  <td className="text-xs">{(p.archived_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="text-xs">{p.archived_by}</td>
                  <td className="text-xs italic">{p.archive_reason}</td>
                </tr>
              ))}
            </tbody></table>
              )
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">MFPL Tax Invoices (Phase 2)</CardTitle>
          <div className="flex items-center gap-2">
            {isSA && <Button size="sm" onClick={() => setPickerMode('INVOICE')} data-testid="btn-create-invoice-legacy" variant="ghost" className="text-[10px] text-muted-foreground">Legacy Setup</Button>}
            <Button size="sm" onClick={() => setInvoiceMasterWizard({ pickerOpen: true })} data-testid="btn-create-invoice"><Plus className="w-4 h-4 mr-1" />Create Invoice</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* iter252 — Selected Invoice Masters: compact name-only list. */}
          {invSnapshots.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2 text-muted-foreground">Selected Invoice Masters for this Franchise</div>
              <div className="flex flex-wrap gap-1.5" data-testid="inv-master-list">
                {invSnapshots.map(s => (
                  <span
                    key={s.id}
                    className="inline-flex items-center px-2 py-1 rounded-full border bg-muted/20 text-xs font-medium"
                    data-testid={`inv-master-chip-${s.worksheet_name.replace(/\s+/g, '-')}`}
                  >{s.worksheet_name}</span>
                ))}
              </div>
            </div>
          )}
          {invSnapshots.length === 0 && (
            <div className="border border-dashed rounded p-3 text-xs text-muted-foreground text-center">
              No Invoice Masters selected. Go to <b>Franchise Setup</b> tab and pick the invoice masters that apply to this franchise.
            </div>
          )}
          {invs.length === 0 ? <div className="text-xs text-muted-foreground">No invoices yet</div> : (
            <table className="w-full text-xs"><thead className="text-left border-b"><tr><th className="p-1">Invoice #</th><th>Type</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th><th className="w-64">Actions</th></tr></thead><tbody>
              {invs.map(i => (
                <tr key={i.id} className="border-b" data-testid={`inv-row-${i.invoice_number}`}>
                  <td className="p-1 font-mono">{i.invoice_number}</td>
                  <td>{i.invoice_type}</td>
                  <td>{currency(i.totals?.final_amount, cur)}</td>
                  <td>{currency(i.paid_amount, cur)}</td>
                  <td>{currency(i.balance_amount, cur)}</td>
                  <td><Badge variant="outline" className="text-[10px]">{i.status}</Badge></td>
                  <td className="flex gap-1 flex-wrap">
                    {i.status === 'DRAFT' && <Button size="sm" variant="ghost" onClick={() => approveInvoice(i.id)} data-testid={`btn-approve-inv-${i.invoice_number}`}>Approve</Button>}
                    {['APPROVED', 'PARTIALLY_PAID'].includes(i.status) && i.balance_amount > 0 && <Button size="sm" variant="ghost" onClick={() => recordPayment(i)} data-testid={`btn-pay-inv-${i.invoice_number}`}>Pay</Button>}
                    <Button size="sm" variant="outline" onClick={() => downloadPDF(`/invoice/pdf/${i.id}`, `Invoice_${i.invoice_number.replace(/\//g, '-')}.pdf`)} data-testid={`btn-dl-inv-${i.invoice_number}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                    {i.status !== 'CANCELLED' && <Button size="sm" variant="outline" onClick={() => emailDoc('INVOICE', i.id, i.invoice_number)} data-testid={`btn-email-inv-${i.invoice_number}`}><Mail className="w-3 h-3 mr-1" />Email</Button>}
                    {i.status !== 'CANCELLED' && (i.paid_amount || 0) === 0 && <Button size="sm" variant="ghost" onClick={() => deleteInvoice(i.id, i.invoice_number)} data-testid={`btn-del-inv-${i.invoice_number}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody></table>
          )}
        </CardContent>
      </Card>

      {/* Working Capital */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Working Capital (Phase 2)</CardTitle>
          <Button size="sm" onClick={upsertWC} data-testid="btn-update-wc"><Edit2 className="w-4 h-4 mr-1" />Update</Button>
        </CardHeader>
        <CardContent className="grid grid-cols-4 gap-2 text-xs">
          <div><div className="text-muted-foreground">Required</div><div className="font-semibold">{currency(wc.required_amount, cur)}</div></div>
          <div><div className="text-muted-foreground">Received</div><div className="font-semibold">{currency(wc.received_amount, cur)}</div></div>
          <div><div className="text-muted-foreground">Balance</div><div className="font-semibold">{currency(wc.balance_amount, cur)}</div></div>
          <div><div className="text-muted-foreground">Ref</div><div>{wc.reference || '—'}</div></div>
        </CardContent>
      </Card>

      {/* Attached Documents */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Attached Documents ({docs.length})</CardTitle>
          <div className="text-xs text-muted-foreground">Auto-attaches to Franchise Documents on generation</div>
        </CardHeader>
        <CardContent>
          {docs.length === 0 ? <div className="text-xs text-muted-foreground">No documents generated yet. Every PO / Invoice / Certificate / Pack PDF you download will appear here and on the Franchise's Documents section automatically.</div> : (
            <table className="w-full text-xs"><thead className="text-left border-b"><tr><th className="p-1">Kind</th><th>Number</th><th>Filename</th><th>Size</th><th>Generated</th><th></th></tr></thead><tbody>
              {docs.map(d => (
                <tr key={d.id} className="border-b" data-testid={`doc-row-${d.id}`}>
                  <td className="p-1"><Badge variant="outline" className="text-[10px]">{d.doc_kind}</Badge></td>
                  <td className="font-mono">{d.number}</td>
                  <td className="truncate max-w-xs">{d.filename}</td>
                  <td>{(d.byte_size / 1024).toFixed(1)} KB</td>
                  <td>{(d.generated_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td><Button size="sm" variant="ghost" onClick={() => downloadPDF(`/document/${d.id}/download`, d.filename)} data-testid={`btn-dl-doc-${d.id}`}><Download className="w-3 h-3" /></Button></td>
                </tr>
              ))}
            </tbody></table>
          )}
        </CardContent>
      </Card>

      {/* Phase 3 Actions */}
      <Card>
        <CardHeader><CardTitle className="text-base">Opening Registers (Phase 3)</CardTitle></CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowInteriorAsset(true)} data-testid="btn-add-interior-asset"><Home className="w-4 h-4 mr-1" />Add Interior / Owner Asset</Button>
          <Button size="sm" onClick={freezeAssets} data-testid="btn-freeze-assets">Freeze Opening Asset Register</Button>
          <Button size="sm" onClick={freezeInventory} data-testid="btn-freeze-inventory">Freeze Opening Inventory</Button>
          {onboarding.asset_opening_frozen && <Badge className="bg-green-600" data-testid="badge-assets-frozen">Assets Frozen</Badge>}
          {onboarding.inventory_opening_frozen && <Badge className="bg-green-600" data-testid="badge-inv-frozen">Inventory Frozen</Badge>}
        </CardContent>
      </Card>

      {/* iter310 (C2) — Vendor invoices matched to PO lines */}
      <VendorInvoicesCard token={token} oid={oid} isSA={isSA} onChanged={() => { loadAll(); onReload && onReload(); }} />

      {/* iter253 — Phase 4: Pre-Opening Checklist (persistent status) */}
      <Card>
        <CardHeader><CardTitle className="text-base">Pre-Opening Sections (Phase 4)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {['agreement', 'staff_mapping', 'pos_mapping', 'aggregator_mapping', 'compliance', 'premises_condition', 'pre_opening_tasks'].map(sec => {
            const info = (onboarding.section_status || {})[sec] || {};
            // iter254 — Prefer auto-computed status; fallback to
            // legacy section_status flag for sections that are still
            // manually marked (e.g. `agreement`).
            const auto = readiness?.auto_status?.[sec];
            const isAuto = !!auto;
            const status = isAuto ? auto.status : (info.status === 'DONE' ? 'COMPLETED' : info.status || 'NOT_STARTED');
            const done = status === 'COMPLETED' || status === 'DONE' || status === 'FROZEN' || status === 'NOT_APPLICABLE';
            const label = sec.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const fmt = (iso) => {
              if (!iso) return '';
              try {
                const d = new Date(iso);
                return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', ' ·');
              } catch { return iso; }
            };
            return (
              <div key={sec} data-testid={`preopen-row-${sec}`} className={`border rounded p-3 ${done ? 'bg-green-50 border-green-300' : 'bg-muted/10'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {done
                      ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                      : <Circle className="w-5 h-5 text-muted-foreground" />}
                    <div>
                      <div className="font-semibold text-sm">{label}{isAuto && <span className="text-[10px] text-muted-foreground ml-2">· auto</span>}</div>
                      {done && !isAuto && (
                        <div className="text-[11px] text-muted-foreground" data-testid={`preopen-meta-${sec}`}>
                          {fmt(info.updated_at)}
                          {info.updated_by ? ` · ${info.updated_by}` : ''}
                        </div>
                      )}
                      {isAuto && auto.counts?.total > 0 && (
                        <div className="text-[11px] text-muted-foreground">{auto.counts.done}/{auto.counts.total} complete</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={done ? 'bg-green-600' : 'bg-muted text-muted-foreground'} data-testid={`preopen-badge-${sec}`}>{status}</Badge>
                    {!done && !isAuto && <Button size="sm" variant="outline" onClick={() => markSectionDone(sec)} data-testid={`btn-mark-${sec}`}>Mark DONE</Button>}
                    {isAuto && ['staff_mapping','pos_mapping','aggregator_mapping','compliance','premises_condition','pre_opening_tasks'].includes(sec) && (
                      <Button size="sm" variant="outline" onClick={() => setSetupSection(sec)} data-testid={`btn-setup-${sec}`}>
                        {sec === 'premises_condition' || sec === 'pre_opening_tasks' ? 'Open Checklist' : `Setup ${sec.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
                      </Button>
                    )}
                  </div>
                </div>
                {isAuto && auto.blockers?.length > 0 && !done && (
                  <ul className="mt-2 ml-8 text-[11px] text-red-700 list-disc list-inside space-y-0.5" data-testid={`preopen-blockers-${sec}`}>
                    {auto.blockers.slice(0, 3).map((b, i) => <li key={i}>{b.label}{b.detail ? ` — ${b.detail}` : ''}</li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* iter253+iter254 — Go-Live gated on readiness (auto-computed) + frozen registers */}
      {(() => {
        const isLive = onboarding.status === 'LIVE';
        // iter254 — Use backend readiness engine as single source of
        // truth.  mandatory_blockers already excludes non-mandatory
        // sections (aggregator/compliance) and knows about frozen
        // registers via _auto_module_status.
        const pending = (readiness?.mandatory_blockers || []).map(b => {
          const detail = (b.blockers && b.blockers[0]?.label) || b.status || '';
          return `${b.label}${detail ? ` — ${detail}` : ''}`;
        });
        const ready = pending.length === 0 && !!readiness;
        return (
          <Card>
            <CardHeader><CardTitle className="text-base">Go-Live (Phase 4)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {isLive ? (
                <div className="border border-green-400 bg-green-50 rounded p-3 flex items-center gap-2" data-testid="golive-status-live">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div className="text-sm"><b className="text-green-800">LIVE</b> — Go-Live Date: <b>{onboarding.operational_go_live_date}</b>
                    {onboarding.operational_active
                      ? <span className="ml-2 text-emerald-700 font-semibold" data-testid="golive-operational-badge">· OPERATIONAL</span>
                      : <span className="ml-2 text-amber-700 font-semibold" data-testid="golive-pending-activation-badge">· PENDING ACTIVATION</span>}
                  </div>
                </div>
              ) : ready ? (
                <div className="border border-green-400 bg-green-50 rounded p-3 flex items-center gap-2" data-testid="golive-status-ready">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <div className="text-sm"><b className="text-green-800">PRE-OPENING VERIFIED</b> — All mandatory requirements completed.</div>
                </div>
              ) : (
                <div className="border border-red-300 bg-red-50 rounded p-3" data-testid="golive-status-blocked">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <div className="text-sm font-semibold text-red-800">GO-LIVE BLOCKED — {pending.length} requirement{pending.length === 1 ? '' : 's'} pending</div>
                  </div>
                  <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5" data-testid="golive-pending-list">
                    {pending.map(p => <li key={p}>{p}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={confirmGoLive}
                  disabled={isLive || !ready}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  data-testid="btn-confirm-go-live"
                >Confirm Go-Live</Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => downloadPDF(`/go-live/certificate/${oid}`, `Go_Live_Certificate_${oid}.pdf`)}
                  disabled={!isLive}
                  data-testid="btn-dl-certificate"
                ><Download className="w-4 h-4 mr-1" />Go-Live Certificate PDF</Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => downloadPDF(`/onboarding-pack/${oid}`, `Onboarding_Pack_${oid}.pdf`)}
                  disabled={!isLive}
                  data-testid="btn-dl-pack"
                ><Download className="w-4 h-4 mr-1" />Complete Onboarding Pack PDF</Button>
                {/* iter261 — Activate for Operations (MGT only, post go-live) */}
                {isLive && !onboarding.operational_active && (
                  <Button
                    size="sm"
                    onClick={activateCentre}
                    className="bg-indigo-700 hover:bg-indigo-800 text-white"
                    data-testid="btn-activate-centre-ops"
                  >Activate Centre for Operations</Button>
                )}
                {isLive && onboarding.operational_active && (
                  <Button
                    size="sm" variant="outline"
                    onClick={deactivateCentre}
                    className="text-rose-700 border-rose-300"
                    data-testid="btn-deactivate-centre-ops"
                  >Deactivate (Hide from Operations)</Button>
                )}
              </div>
              {isLive && !onboarding.operational_active && (
                <div className="border border-amber-300 bg-amber-50 rounded p-2 text-[11px] text-amber-800"
                       data-testid="golive-activation-hint">
                  <b>Next step:</b> Click <em>Activate Centre for Operations</em> to make
                  this centre available across Login, Sales, Attendance, Salary,
                  Inventory, Accounts, Booking, Consumption and every other
                  operational dropdown.
                </div>
              )}
              {!isLive && !ready && (
                <div className="text-[11px] text-muted-foreground italic">Certificate + Complete Onboarding Pack will be available once the centre is LIVE.</div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {pickerMode && (
        <DocPickerModal
          call={call} token={token} oid={oid} mode={pickerMode}
          heads={heads} lines={lines}
          onboarding={onboarding}
          poRefOptions={pos}
          onClose={() => setPickerMode(null)}
          onCreated={() => { setPickerMode(null); loadAll(); }}
        />
      )}

      {/* iter243 — PO Wizard V2 (Excel-driven) + iter248 category preset */}
      <POWizardV2
        open={showPOWizard}
        onClose={() => { setShowPOWizard(false); setWizardPreset(null); }}
        call={call} token={token} oid={oid} onboarding={onboarding}
        preset={wizardPreset}
        onCreated={() => { loadAll(); }}
      />

      {/* iter248 — Invoice category-driven wizard */}
      {invoiceWizard && (
        <CategoryInvoiceWizard
          open={!!invoiceWizard}
          onClose={() => setInvoiceWizard(null)}
          call={call} token={token} oid={oid} onboarding={onboarding}
          category={invoiceWizard}
          onCreated={() => { setInvoiceWizard(null); loadAll(); }}
        />
      )}
      {/* iter243 — DANGER ZONE (SA only) */}
      {isSA && (
        <Card className="border-red-300 bg-red-50/40" data-testid="danger-zone-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base text-red-700">⚠ Danger Zone</CardTitle>
            <Button size="sm" variant="destructive"
              onClick={async () => {
                try {
                  const impact = await call(`/danger/delete-impact/${oid}`, {});
                  setDangerImpact(impact); setShowDanger(true);
                  setDangerForm({ reason: '', typed: '', acknowledged: false });
                } catch (e) { toast.error(e.message); }
              }}
              data-testid="btn-danger-open">
              <Trash2 className="w-4 h-4 mr-1" />Delete Entire Onboarding / Franchise
            </Button>
          </CardHeader>
          <CardContent className="text-xs text-red-800">
            Removes this test/wrong franchise from every active view. Requires typed confirmation. Cannot delete a centre with real operational data.
          </CardContent>
        </Card>
      )}

      {/* Danger Zone confirmation modal */}
      <Dialog open={showDanger} onOpenChange={setShowDanger}>
        <DialogContent className="max-w-2xl" data-testid="danger-modal">
          <DialogHeader><DialogTitle className="text-red-700">Delete Entire Onboarding — {onboarding?.franchise_code}</DialogTitle></DialogHeader>
          {dangerImpact?.operational_data_exists ? (
            <div className="bg-amber-100 border border-amber-400 rounded p-3 text-sm text-amber-900" data-testid="danger-ops-blocked">
              <b>Blocked:</b> Operational data exists for this centre.<br/>
              {Object.entries(dangerImpact.operational_data).map(([k, v]) => (
                <div key={k}>{k}: {v}</div>
              ))}
              <div className="mt-2">This centre cannot be deleted through the onboarding module — please use the separate SA archival / migration process for live franchises.</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-red-100 border border-red-400 rounded p-2 text-xs text-red-900" data-testid="danger-impact">
                <b>Impact Summary:</b>
                <ul className="list-disc pl-4 mt-1">
                  {dangerImpact && Object.entries(dangerImpact.counts).map(([k, v]) => (
                    <li key={k}>{k.replace(/_/g, ' ')}: <b>{v}</b></li>
                  ))}
                </ul>
              </div>
              <div>
                <Label>Reason <span className="text-red-500">*</span></Label>
                <Textarea rows={2} value={dangerForm.reason}
                  onChange={e => setDangerForm(f => ({ ...f, reason: e.target.value }))}
                  data-testid="danger-reason" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={dangerForm.acknowledged}
                  onChange={e => setDangerForm(f => ({ ...f, acknowledged: e.target.checked }))}
                  data-testid="danger-ack" />
                <label className="text-xs">I understand that this will remove this onboarding record from all active operational views.</label>
              </div>
              <div>
                <Label>Type <span className="font-mono bg-red-100 px-1">DELETE {onboarding?.franchise_code}</span> to confirm</Label>
                <Input value={dangerForm.typed}
                  onChange={e => setDangerForm(f => ({ ...f, typed: e.target.value }))}
                  data-testid="danger-typed" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDanger(false)}>Cancel</Button>
            {!dangerImpact?.operational_data_exists && (
              <Button variant="destructive"
                disabled={!dangerForm.acknowledged || !dangerForm.reason.trim() || dangerForm.typed !== `DELETE ${onboarding?.franchise_code}`}
                onClick={async () => {
                  try {
                    await call(`/danger/delete-onboarding/${oid}`, {
                      reason: dangerForm.reason.trim(),
                      typed_confirmation: dangerForm.typed,
                      acknowledged: dangerForm.acknowledged,
                    });
                    toast.success(`Onboarding ${onboarding?.franchise_code} archived`);
                    setShowDanger(false);
                    onReload && onReload();
                  } catch (e) { toast.error(e.message); }
                }}
                data-testid="btn-danger-confirm">
                Permanently Archive
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter251 — Snapshot Editor (Edit Items) */}
      {snapshotEditor && (
        <Dialog open={!!snapshotEditor} onOpenChange={() => setSnapshotEditor(null)}>
          <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
            <SnapshotEditor
              snapshot={snapshotEditor.snapshot}
              onSaved={loadAll}
              onClose={() => setSnapshotEditor(null)}
              token={token}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* iter251 — Invoice Master Wizard (Create Invoice from selected snapshot) */}
      {invoiceMasterWizard?.pickerOpen && (
        <Dialog open onOpenChange={() => setInvoiceMasterWizard(null)}>
          <DialogContent className="max-w-lg" data-testid="inv-master-picker">
            <DialogHeader><DialogTitle>Create Invoice — Select Invoice Master</DialogTitle></DialogHeader>
            {invSnapshots.length === 0 ? (
              <div className="text-sm text-muted-foreground">No Invoice Masters selected for this franchise. Go to <b>Franchise Setup</b> and pick invoice masters first.</div>
            ) : (
              <div className="space-y-2">
                <Label>Invoice Master <span className="text-red-500">*</span></Label>
                <Select onValueChange={async (v) => {
                  try {
                    const r = await fetch(`${API}/api/masters/franchise/snapshot/${v}`, {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ token }),
                    }).then(r => r.json());
                    setInvoiceMasterWizard({ snapshot: r });
                  } catch (e) { toast.error(e.message); }
                }}>
                  <SelectTrigger data-testid="inv-master-picker-select"><SelectValue placeholder="Select Invoice Master" /></SelectTrigger>
                  <SelectContent>
                    {invSnapshots.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.worksheet_name} · {s.row_count} items</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setInvoiceMasterWizard(null)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {invoiceMasterWizard?.snapshot && (
        <GenerateInvoiceWizard
          snapshot={invoiceMasterWizard.snapshot}
          onboarding={onboarding}
          token={token}
          onClose={() => setInvoiceMasterWizard(null)}
          onGenerated={() => { setInvoiceMasterWizard(null); loadAll(); }}
        />
      )}
      {/* iter255 (C1) — Interior / Owner Asset entry */}
      <InteriorAssetModal
        open={showInteriorAsset}
        onClose={() => setShowInteriorAsset(false)}
        onboardingId={oid}
        token={token}
        onSaved={() => { loadAll(); onReload && onReload(); }}
      />
      {/* iter256 — Section Setup modal (staff / pos / aggregator / compliance / premises / preopen) */}
      <SectionSetupModal
        open={!!setupSection}
        sectionKey={setupSection}
        onboardingId={oid}
        token={token}
        onClose={() => setSetupSection(null)}
        onSaved={() => { loadAll(); onReload && onReload(); }}
      />
    </div>
  );
}
export default function FranchiseOnboarding() {
  const { session } = useAuth();
  const token = session?.token;
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [listKey, setListKey] = useState(0);

  // iter238 — bind the API caller to the live session token from the
  // app-level AuthContext.  If the session has not yet hydrated we
  // render a friendly notice instead of firing unauthenticated calls.
  const call = React.useMemo(() => makeCall(token), [token]);

  if (!token) {
    return (
      <div className="p-8" data-testid="onboarding-no-session">
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          Authenticating session… If this persists, please sign in again from the top-right menu.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="franchise-onboarding-root">
      <div>
        <h2 className="text-2xl font-bold">Franchise Onboarding & Centre Activation</h2>
        <p className="text-sm text-muted-foreground">Single source of truth for setting up every new Purnabramha centre — from Commercial Setup through Go-Live.</p>
      </div>
      {openId
        ? <OnboardingDetail call={call} token={token} oid={openId} onBack={() => { setOpenId(null); setListKey(k => k + 1); }} />
        : <OnboardingList key={listKey} call={call} onOpen={id => setOpenId(id)} onCreate={() => setShowCreate(true)} />}
      <CreateFranchiseModal call={call} open={showCreate} onClose={() => setShowCreate(false)} onCreated={id => { setShowCreate(false); setOpenId(id); }} />
    </div>
  );
}
